import { useState, useEffect } from "react";
import { serverNow } from "../serverClock";
import { formatWhen } from "./Shared";

/**
 * How long is left, counted against the SERVER's clock.
 *
 * Not `Date.now()`. A quotation's deadline decides whether somebody can still
 * sign, and that decision is made on the server — so a countdown read off the
 * viewer's laptop can say four hours remain while the offer has already lapsed,
 * or the reverse. `serverNow()` is fed from the `Date` header of every API
 * response, which is by definition "the server, just now".
 *
 * The same mistake cost this repo a debugging session once already: the Jobs
 * page learned its offset from a row timestamp, on the assumption that the row
 * was rewritten every couple of seconds. It was, for uploads, and never for
 * packaging — so one frozen row made every job on the page read 0s.
 */
export function remainingText(deadlineMs, nowMs) {
  const ms = deadlineMs - nowMs;
  if (ms <= 0) return null;
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days >= 1) return `${days} day${days === 1 ? "" : "s"}${hours ? `, ${hours} h` : ""}`;
  if (hours >= 1) return `${hours} h ${minutes % 60} min`;
  return `${minutes} min`;
}

// `zone` is the portal's display timezone, for the tooltip only. It was
// `toLocaleString()` until 2026-10-30: the reader's own zone and, under en-US,
// "11:59:59 PM" — the one time of day in the portal that was not HH:MM.
export default function Countdown({ until, expiredLabel = "expired", style, zone }) {
  // Re-rendered every 30 s rather than every second. The unit displayed is
  // never finer than a minute, so a faster tick would repaint the same string.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  if (!until) return null;
  const deadline = new Date(until).getTime();
  if (Number.isNaN(deadline)) return null;

  const left = remainingText(deadline, serverNow());
  const urgent = left && deadline - serverNow() < 48 * 3600 * 1000;
  return (
    <span
      title={formatWhen(until, { zone })}
      style={{ fontSize: 12, fontWeight: 600,
               color: left ? (urgent ? "#b45309" : "#6b7280") : "#dc2626", ...style }}
    >
      {left ? `${left} left` : expiredLabel}
    </span>
  );
}
